import React from "react";
import styled from "styled-components";
import PersonIcon from "@mui/icons-material/Person";

// Types
interface TravellerDetail {
  ticket_type: string;
  age: number;
  nationality: string;
  iso: string;
  numeric: number;
  continent: string;
  free_entrance: boolean;
}

interface TravellerDetailsDisplayProps {
  travellerDetails: TravellerDetail[];
}

// Styled Components
const TravellerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
  border-radius: 5px;
  padding: 8px;
  overflow-x: auto;
`;

const TravellerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 10px;
`;

const HeaderTitle = styled.span`
  font-weight: bold;
  color: #298beb;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const TravellerCount = styled.span`
  background-color: #f0f7ff;
  color: #0066cc;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: bold;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;

  th {
    padding: 10px;
    text-align: left;
    font-weight: 600;
    color: #555;
    position: sticky;
    top: 0;
    background-color: #f5f5f5;
    z-index: 1;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
  }
`;

const TableBody = styled.tbody`
  tr {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f9f9f9;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #f0f0f0;
    }
  }

  td {
    padding: 10px;
    vertical-align: middle;
    border-bottom: 1px solid #f0f0f0;
  }
`;

const FreeEntrance = styled.span<{
  isFree: boolean;
}>`
  background-color: ${(props) => (props.isFree ? "#e6f7e6" : "#ffeaef")};
  color: ${(props) => (props.isFree ? "#2e7d32" : "#d32f2f")};
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
`;

const RowNumber = styled.div`
  background-color: #eee;
  color: #555;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const CountryCode = styled.span`
  color: #666;
  font-size: 12px;
  white-space: nowrap;
`;

// Component
const TravellerDetailsDisplay: React.FC<TravellerDetailsDisplayProps> = ({
  travellerDetails,
}) => {
  if (!travellerDetails || travellerDetails.length === 0) {
    return null;
  }

  return (
    <TravellerWrapper>
      <TravellerHeader>
        <HeaderTitle>
          <PersonIcon fontSize="small" />
          TRAVELLER TICKET DETAILS
        </HeaderTitle>
        <TravellerCount>
          {travellerDetails.length} Traveller
          {travellerDetails.length !== 1 ? "s" : ""}
        </TravellerCount>
      </TravellerHeader>

      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <th style={{ width: "40px" }}>#</th>
              <th>Ticket Type</th>
              <th>Age</th>
              <th>Nationality</th>
              <th>Continent</th>
              <th>Country Code</th>
              <th>Status</th>
            </tr>
          </TableHead>
          <TableBody>
            {travellerDetails.map((traveller, index) => (
              <tr key={index}>
                <td>
                  <RowNumber>{index + 1}</RowNumber>
                </td>
                <td>
                  <strong>{traveller.ticket_type}</strong>
                </td>
                <td>{traveller.age}</td>
                <td>{traveller.nationality}</td>
                <td>{traveller.continent}</td>
                <td>
                  <CountryCode>
                    {traveller.iso} ({traveller.numeric})
                  </CountryCode>
                </td>
                <td>
                  <FreeEntrance isFree={traveller.free_entrance}>
                    {traveller.free_entrance
                      ? "Free Entrance"
                      : "Paid Entrance"}
                  </FreeEntrance>
                </td>
              </tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TravellerWrapper>
  );
};

export default TravellerDetailsDisplay;
